import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { ApolloClient } from '@apollo/client';
import type { TenantShop } from '@core/tenant/types';
import { MULTI_SHOP_ENABLED } from '@core/config/shops';
import {
  getDefaultGraphqlClient,
  getGraphqlClientsByShop,
  getGraphqlClientForShop,
} from '@core/api/graphqlClient';
import { useTenantShops } from '@core/tenant/TenantShopsContext';

export interface ApolloClientsContextValue {
  multiShop: boolean;
  shops: TenantShop[];
  tenantLoading: boolean;
  defaultClient: ApolloClient;
  clientsByShop: Map<string, ApolloClient>;
  /** Multiplexed backends removed; parameter ignored. */
  getClient: (_shopId: string) => ApolloClient;
}

const ApolloClientsContext = createContext<ApolloClientsContextValue | null>(
  null
);

export function ApolloClientsProvider({ children }: { children: ReactNode }) {
  const { shops, loading: tenantLoading } = useTenantShops();

  const value = useMemo((): ApolloClientsContextValue => {
    const clientsByShop = getGraphqlClientsByShop();
    return {
      multiShop: MULTI_SHOP_ENABLED,
      shops,
      tenantLoading,
      defaultClient: getDefaultGraphqlClient(),
      clientsByShop,
      getClient: getGraphqlClientForShop,
    };
  }, [shops, tenantLoading]);

  return (
    <ApolloClientsContext.Provider value={value}>
      {children}
    </ApolloClientsContext.Provider>
  );
}

export function useApolloClients(): ApolloClientsContextValue {
  const ctx = useContext(ApolloClientsContext);
  if (!ctx) {
    throw new Error('useApolloClients must be used within ApolloClientsProvider');
  }
  return ctx;
}
